import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../../shared/components/Motion";
import { ButtonComponent } from "shared/components";
import { useAdmin } from "../AdminProvider";
import { modalVariants, childVariants } from "shared/utils";
import "../styles/EditCertificatesModal.css";

const EditContactModal = ({
  isOpen,
  onClose,
  contactType,
  contactData,
  publishedData,
  onSave,
}) => {
  const { saveContactDraft } = useAdmin();
  const [formData, setFormData] = useState({
    value: "",
    link: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (contactData && contactType) {
        setFormData({
          value: contactData[contactType]?.value || "",
          link: contactData[contactType]?.link || "",
        });
      } else {
        setFormData({
          value: "",
          link: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, contactData, contactType]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.value.trim()) newErrors.value = "Value is required";
    if (!formData.link.trim()) newErrors.link = "Link is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const updatedContact = {
      ...publishedData,
      [contactType]: {
        value: formData.value,
        link: formData.link,
      },
    };

    try {
      await saveContactDraft(updatedContact);
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving contact draft:", error);
    }
  };

  const getTitle = () => {
    const titles = {
      email: "Email",
      whatsapp: "WhatsApp",
      messenger: "Messenger",
    };
    return titles[contactType] || "Contact";
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "500px",
              width: "90%",
            }}
          >
            <i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
            ></i>

            <motion.h3
              className="preview-modal-title"
              variants={childVariants}
              initial="hidden"
              animate="visible"
            >
              Edit {getTitle()}
            </motion.h3>

            {/* Value Input */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{
                position: "relative",
                marginBottom: "1rem",
              }}
            >
              <label
                className="preview-modal-subtitle"
                style={{
                  fontWeight: "600",
                  fontSize: "small",
                }}
              >
                {contactType === "email"
                  ? "Email Address"
                  : contactType === "whatsapp"
                  ? "Phone Number"
                  : "Username"}{" "}
                *
              </label>
              <input
                className="glass__card"
                type={contactType === "email" ? "email" : "text"}
                name="value"
                value={formData.value}
                onChange={handleInputChange}
                placeholder={
                  contactType === "email"
                    ? "e.g., example@gmail.com"
                    : contactType === "whatsapp"
                    ? "e.g., +919876543210"
                    : "e.g., username"
                }
                style={{
                  width: "100%",
                  height: "2.5rem",
                  marginTop: "0.5rem",
                  padding: "0 1rem",
                  border: "2px solid var(--title-color)",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  backgroundColor: "var(--body-color)",
                  color: "var(--border-color)",
                  outline: "none",
                }}
              />
              {errors.value && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "0.7rem",
                    marginTop: "0.25rem",
                    display: "block",
                  }}
                >
                  {errors.value}
                </span>
              )}
            </motion.div>

            {/* Link Input */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{
                position: "relative",
                marginBottom: "1rem",
              }}
            >
              <label
                className="preview-modal-subtitle"
                style={{
                  fontWeight: "600",
                  fontSize: "small",
                }}
              >
                Link *
              </label>
              <input
                className="glass__card"
                type="url"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                placeholder="e.g., https://example.com"
                style={{
                  width: "100%",
                  height: "2.5rem",
                  marginTop: "0.5rem",
                  padding: "0 1rem",
                  border: "2px solid var(--title-color)",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  backgroundColor: "var(--body-color)",
                  color: "var(--border-color)",
                  outline: "none",
                }}
              />
              {errors.link && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "0.7rem",
                    marginTop: "0.25rem",
                    display: "block",
                  }}
                >
                  {errors.link}
                </span>
              )}
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
            >
              <ButtonComponent
                buttonTitle="Cancel"
                buttonText="Cancel"
                onClick={onClose}
              />
              <ButtonComponent
                buttonTitle="Save Changes"
                buttonText="Save Changes"
                onClick={handleSave}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default EditContactModal;
